import { fontStack } from './fonts.js';
import type { MeasureText } from '@personalizehq/schemas/ir';

// THE ONE CANVAS MEASURER IN THE CODEBASE. The production renderer, the golden-regeneration
// tools, and the spike's CSS-flow harness arm all import THIS function — three copies collapsed
// to one, because a drifted copy would silently move wrap points.
//
// This is the web implementation of the injected platform primitive (SPEC §6.6): Canvas
// TextMetrics + greedy word wrap, with an over-long word BROKEN BY CHARACTER. That last part is
// NORMATIVE: Core Text, Skia and Minikin all char-break by default, while a word-only wrapper —
// and CSS's default `overflow-wrap: normal` — let the word overflow on ONE line. A line-count
// difference is a height difference; height cascades.

/// Grapheme clusters in `s` — Swift's `String.count`, so the two arms count the same "n".
/// `Intl.Segmenter` with no options is UAX #29 extended grapheme clusters, the same segmentation
/// Swift's `Character` uses; it ships in every browser that can run the dashboard.
const segmenter = new Intl.Segmenter();
function clusterCount(s: string): number {
  let n = 0;
  for (const _ of segmenter.segment(s)) n++;
  return n;
}

export function makeMeasureText(): MeasureText {
  const g = document.createElement('canvas').getContext('2d')!;
  return (text, font, maxWidth) => {
    // THE SAME STACK THE PAINTER USES — see `fontStack`. Measure with one family and paint
    // with another and every wrap point moves. `style` joins the shorthand for the same reason:
    // an italic face has its own advances, and SPEC §4.1 forbids synthesising one, so if the face
    // is absent the browser falls back to upright here AND in the painter, together.
    g.font = `${font.style === 'italic' ? 'italic ' : ''}${font.weight} ${font.size}px ${fontStack(font.family)}`;

    // TRACKING IS COMPUTED, NOT DELEGATED (SPEC §6.4b). `ctx.letterSpacing` adds a gap after the
    // FINAL character, so every tracked string would measure one gap too wide — and it is a silent
    // no-op on older WebKit, which is the browser every iOS in-app browser runs. `(n - 1) × spacing`
    // is the whole rule; a single character adds nothing, and an empty string adds nothing.
    //
    // "n" counts GRAPHEME CLUSTERS, because that is the unit the painters gap: CSS `letter-spacing`
    // spaces typographic character units and Core Text's `kern` spaces glyph clusters, so counting
    // code points would charge a ZWJ emoji four gaps that no renderer ever draws — and the painted
    // line count would walk away from the measured one on an exact gate.
    const track = (s: string): number => (font.letterSpacing === 0 ? 0 : Math.max(0, clusterCount(s) - 1) * font.letterSpacing);
    const width = (s: string): number => g.measureText(s).width + track(s);

    const full = width(text);
    if (!isFinite(maxWidth) || full <= maxWidth) return { w: full, line_count: 1 };

    let lines = 1;
    let cur = '';
    for (const word of text.split(' ')) {
      const next = cur ? `${cur} ${word}` : word;
      if (width(next) <= maxWidth) {
        cur = next;
        continue;
      }
      if (cur) {
        lines++; // the word starts a fresh line
        cur = '';
      }
      // Place the word alone, splitting it if it STILL does not fit.
      let chunk = '';
      for (const ch of word) {
        const grown = chunk + ch;
        if (chunk && width(grown) > maxWidth) {
          lines++;
          chunk = ch;
        } else {
          chunk = grown;
        }
      }
      cur = chunk;
    }
    return { w: maxWidth, line_count: lines };
  };
}
